import axios, { AxiosResponse, AxiosError } from 'axios';

export interface PaymentCard {
  number: string;
  recipient_name?: string;
  bank_name?: string;
}

export interface PaymentData {
  id: string;
  amount: number;
  currency: string;
  status: string;
  card?: PaymentCard;
  details: {
    redirect_url?: string;
    cancel_redirect_url?: string;
    support_url?: string;
  };
  expires_at: string;
  created_at: string;
  supports_receipt?: boolean;
}

export interface ConfirmPaymentResponse {
  id: string;
  amount: number;
  currency: string;
  status: string;
  card?: PaymentCard;
  details: {
    redirect_url?: string;
    cancel_redirect_url?: string;
    support_url?: string;
  };
  expires_at: string;
  created_at: string;
  supports_receipt?: boolean;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.pagoseguropro.com/api/v2/internal';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 1000 * 20,
  headers: {
    'Content-Type': 'application/json',
  },
});

const normalizeUrl = (url: string): string => {
  if (url.endsWith('/')) {
    return url;
  }

  const queryIndex = url.indexOf('?');
  if (queryIndex !== -1) {
    return url.slice(0, queryIndex) + '/' + url.slice(queryIndex);
  }

  return url + '/';
};

apiClient.interceptors.request.use(
  (config) => {
    if (config.url && !config.url.includes('confirm') && !config.url.includes('receipt')) {
      config.url = normalizeUrl(config.url);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error: AxiosError) => {
    if (error.response) {
      console.error('Response error:', error.response.status, error.response.data);
    } else if (error.request) {
      console.error('Request error:', error.request);
    } else {
      console.error('Error:', error.message);
    }
    return Promise.reject(error);
  }
);

export interface UploadReceiptRequest {
  filename: string;
  body: string;
}

export interface UploadReceiptResponse {
  success: boolean;
}

export class PaymentApiService {
  static async getPayment(paymentId: string): Promise<PaymentData> {
    try {
      const response = await apiClient.get<PaymentData>(`/payment/${paymentId}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 404) {
          throw new Error('Payment not found');
        } else if (error.response?.status === 401) {
          throw new Error('Unauthorized access');
        } else {
          // @ts-ignore
          if (error.response?.status >= 500) {
            throw new Error('Server error. Please try again later');
          } else if (error.code === 'ECONNABORTED') {
            throw new Error('Request timeout. Please check your connection');
          }
        }
      }

      throw new Error('Failed to fetch payment data');
    }
  }

  static async confirmPayment(paymentId: string): Promise<ConfirmPaymentResponse> {
    try {
      const response = await apiClient.post<ConfirmPaymentResponse>(`/payment/${paymentId}/confirm`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 404) {
          throw new Error('Payment not found');
        } else if (error.response?.status === 401) {
          throw new Error('Unauthorized access');
        } else {
          // @ts-ignore
          if (error.response?.status >= 500) {
            throw new Error('Server error. Please try again later');
          } else if (error.code === 'ECONNABORTED') {
            throw new Error('Request timeout. Please check your connection');
          }
        }
      }

      throw new Error('Failed to confirm payment');
    }
  }

  static async uploadReceipt(paymentId: string, file: File): Promise<UploadReceiptResponse> {
    try {
      const base64 = await PaymentApiService.fileToBase64(file);

      const response = await apiClient.post<UploadReceiptResponse>(
        `/payment/${paymentId}/receipt`,
        {
          filename: file.name,
          body: base64
        }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 400) {
          throw new Error('Invalid file format');
        } else if (error.response?.status === 413) {
          throw new Error('File too large');
        } else if (error.response?.status === 429) {
          throw new Error('Please wait before uploading again');
        } else if (error.response?.status && error.response.status >= 500) {
          throw new Error('Server error. Please try again later');
        }
      }
      throw new Error('Failed to upload receipt');
    }
  }

  private static fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = (error) => reject(error);
    });
  }
}
